import React, { useState, useContext } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import AuthContext from '../../context/AuthContext';

// Material UI components
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@mui/material';

// Material UI icons
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  QrCodeScanner as ScannerIcon
} from '@mui/icons-material';

const Header = () => {
  const { isAuthenticated, user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/login');
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const menuId = 'primary-account-menu';
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      id={menuId}
      keepMounted
      open={Boolean(anchorEl)}
      onClose={handleMenuClose}
      PaperProps={{
        elevation: 3,
        sx: { minWidth: 180 }
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      <MenuItem onClick={handleLogout}>
        <ListItemIcon>
          <LogoutIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Logout</ListItemText>
      </MenuItem>
    </Menu>
  );

  const navigationItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Inventory', icon: <InventoryIcon />, path: '/inventory' },
    { text: 'Suppliers', icon: <BusinessIcon />, path: '/suppliers' },
    { text: 'Locations', icon: <LocationIcon />, path: '/locations' },
    { text: 'Technicians', icon: <PeopleIcon />, path: '/technicians' },
    { text: 'Tech Inventory', icon: <InventoryIcon />, path: '/technician-inventory' }
  ];

  // Add inventory submenu items
  const inventoryItems = [
    { text: 'Adjust Inventory', path: '/inventory/adjust' },
    { text: 'Scan Inventory', path: '/inventory/scan', icon: <ScannerIcon /> }
  ];

  // Add Reports submenu items
  const reportItems = [
    { text: 'Inventory Report', path: '/reports/inventory' },
    { text: 'Reorder Points', path: '/reports/reorder' },
    { text: 'Transaction History', path: '/reports/history' },
    { text: 'Supplier Matrix', path: '/reports/supplier-matrix' }
  ];

  // Add Users management for managers
  if (user && user.role === 'manager') {
    navigationItems.push({ text: 'Users', icon: <PeopleIcon />, path: '/users' });
  }

  const drawer = (
    <Box sx={{ width: 250 }} role="presentation">
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 2,
        bgcolor: 'primary.main',
        color: 'primary.contrastText'
      }}>
        <Typography variant="h6" noWrap component="div">
          HVAC Inventory
        </Typography>
        <IconButton
          color="inherit"
          onClick={handleDrawerToggle}
          edge="end"
        >
          <ChevronLeftIcon />
        </IconButton>
      </Box>
      <Divider />
      <List>
        {navigationItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              onClick={() => handleNavigation(item.path)}
              selected={isActive(item.path)}
            >
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem sx={{ pl: 2, py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Inventory
          </Typography>
        </ListItem>
        {inventoryItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              onClick={() => handleNavigation(item.path)}
              selected={isActive(item.path)}
              sx={{ pl: 4 }}
            >
              {item.icon && <ListItemIcon sx={{ pl: 1 }}>{item.icon}</ListItemIcon>}
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem sx={{ pl: 2, py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Reports
          </Typography>
        </ListItem>
        {reportItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              onClick={() => handleNavigation(item.path)}
              selected={isActive(item.path)}
              sx={{ pl: 4 }}
            >
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="sticky" color="primary" elevation={1}>
        <Toolbar>
          {isAuthenticated && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Typography
            variant="h6"
            noWrap
            component={RouterLink}
            to="/"
            sx={{
              mr: 2,
              display: { xs: 'none', sm: 'flex' },
              fontWeight: 700,
              color: 'inherit',
              textDecoration: 'none',
            }}
          >
            HVAC Parts Inventory
          </Typography>

          <Typography
            variant="h6"
            noWrap
            component={RouterLink}
            to="/"
            sx={{
              mr: 2,
              display: { xs: 'flex', sm: 'none' },
              fontWeight: 700,
              color: 'inherit',
              textDecoration: 'none',
            }}
          >
            HVAC Inventory
          </Typography>

          <Box sx={{ flexGrow: 1 }} />

          {isAuthenticated ? (
            <>
              {!isMobile && (
                <Box sx={{ display: 'flex' }}>
                  {navigationItems.map((item) => (
                    <Button
                      key={item.text}
                      color="inherit"
                      component={RouterLink}
                      to={item.path}
                      sx={{
                        mx: 0.5,
                        borderRadius: 1,
                        ...(isActive(item.path) && {
                          bgcolor: 'rgba(255, 255, 255, 0.12)',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                          },
                        }),
                      }}
                    >
                      {item.text}
                    </Button>
                  ))}
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip title="Scan Inventory">
                  <IconButton
                    size="large"
                    aria-label="scan inventory"
                    onClick={() => handleNavigation('/inventory/scan')}
                    color="inherit"
                    sx={{ mr: 1 }}
                  >
                    <ScannerIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Account settings">
                  <IconButton
                    size="large"
                    edge="end"
                    aria-label="account of current user"
                    aria-controls={menuId}
                    aria-haspopup="true"
                    onClick={handleProfileMenuOpen}
                    color="inherit"
                  >
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: 'secondary.main',
                        fontSize: '0.875rem'
                      }}
                    >
                      {user?.username?.charAt(0).toUpperCase() || 'U'}
                    </Avatar>
                  </IconButton>
                </Tooltip>
              </Box>
            </>
          ) : (
            <Button
              color="inherit"
              component={RouterLink}
              to="/login"
              variant="outlined"
              sx={{ borderColor: 'rgba(255, 255, 255, 0.5)' }}
            >
              Login
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {renderMenu}

      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header;
